import { useContext, useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic search box that filters a result set, with optional fuzzy
 * autocomplete suggestions. Modelled on reactive-json's native
 * `AutocompleteField` (debounced remote fetch, min-chars, outside-click), but
 * behaves as a free-text *filter* rather than an id picker.
 *
 * Commit model — the page refetch is the YAML `actions:` (`submitData`), fired
 * by an **internal hidden button** that is the *only* child of
 * `ActionDependant`. The component clicks that button programmatically at the
 * right moments :
 *   - while typing, after `debounce` ms (when `searchOnType`, default true) ;
 *   - on Enter ;
 *   - when picking a suggestion ;
 *   - when clearing.
 * Because the input/dropdown live *outside* `ActionDependant`, clicking or
 * focusing the field never triggers a refetch — only those explicit commits do.
 *
 * Optional `loadingLocation` is set to `true` as soon as the user types (so a
 * LoadingOverlay can show during the debounce) ; the page's `submitData`
 * should reset it `on: response`.
 *
 * YAML usage:
 *   - type: SearchField
 *     dataLocation: ~~.search
 *     placeholder: 'Rechercher un produit…'
 *     ariaLabel: 'Rechercher un produit'   # nom accessible ; défaut = placeholder
 *     id: 'produit-search'                 # posé sur l'input : ce que vise le
 *                                          # `<label for>` écrit à côté
 *     suggestUrl: ~~.esQueryUrl
 *     suggestionLabel: objectData.data.name
 *     suggestionSecondary: objectData.data.brand
 *     searchOnType: true
 *     debounce: 350
 *     loadingLocation: ~~.esLoading
 *     actions:
 *       - { what: setData, on: click, path: ~~.pagination.from, value: 0 }
 *       - { what: submitData, on: click, requestKey: esResult-refresh, url: ~~.esQueryUrl,
 *           httpMethod: POST, data: { filter: ~~.selectedFilters, search: ~~.search, pagination: ~~.pagination },
 *           updateOnlyData: true, updateDataAtLocation: ~~.esResult, refreshAppOnResponse: true }
 */

const getByPath = (obj, dotPath) =>
  String(dotPath || '')
    .split('.')
    .reduce((node, key) => (node != null && typeof node === 'object' ? node[key] : undefined), obj)

// A resolved field may be a Drupal taxonomy term-ref ({tid, vocabulary, name})
// or a plain scalar — coalesce to a renderable string so we never hand React
// an object child (error #31).
const toLabel = (v) =>
  v && typeof v === 'object' ? v.name ?? v.label ?? v.value ?? '' : v

export const SearchField = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const placeholder = resolve(props.placeholder) ?? 'Rechercher…'
  // Nom accessible du champ (RGAA 11.1 / WCAG 4.1.2) : un `placeholder` seul
  // n'est pas une étiquette — il disparaît à la saisie et n'est pas exposé
  // comme tel. Repli sur le placeholder, faute de mieux, pour que le champ ne
  // soit jamais anonyme.
  const ariaLabel = resolve(props.ariaLabel) ?? placeholder
  // Sans cet identifiant sur l'input, le `<label for>` que le YAML pose à côté
  // ne désigne aucun champ (RGAA 11.1) : l'association est impossible à écrire
  // en YAML tant que la primitive ne transmet pas son `id`.
  const inputId = resolve(props.id)
  const suggestUrl = resolve(props.suggestUrl)
  const suggestSize = Number(resolve(props.suggestSize)) || 6
  const minChars = Number(resolve(props.minChars)) || 2
  const debounceMs = Number(resolve(props.debounce)) || 350
  const searchOnType = resolve(props.searchOnType) !== false
  const labelPath = resolve(props.suggestionLabel) || 'objectData.data.name'
  const secondaryPath = resolve(props.suggestionSecondary)
  const loadingLocation = props.loadingLocation
  const wrapperClass = resolve(props.attributes?.class) ?? 'w-full @2xl:w-64'
  // Classe de l'input — surchargeable pour aligner sur le DS d'un projet (défaut
  // conservé pour ne pas changer les pages existantes). L'icône loupe attend un
  // padding gauche (pl-9) ; garder pl-9 pr-9 dans toute surcharge.
  const inputClass = resolve(props.inputClass)
    ?? 'w-full h-10 pl-9 pr-9 text-sm bg-white border border-gray-200 rounded-full text-gray-700 outline-none focus:ring-2 focus:ring-indigo-400'

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: '',
    globalDataContext,
    templateContext,
  })
  const value = typeof formData === 'string' ? formData : ''

  const loadingPath = loadingLocation
    ? propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: loadingLocation,
        defaultValue: false,
        globalDataContext,
        templateContext,
      }).formDataPath
    : null

  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const debounceRef = useRef(null)
  const fetchSeq = useRef(0)

  const setQuery = (q) => globalDataContext.updateData(q, formDataPath)
  const setLoading = (v) => { if (loadingPath) globalDataContext.updateData(v, loadingPath) }

  // Fire the page's declared submitData via the (isolated) hidden trigger.
  const commit = () => {
    setOpen(false)
    triggerRef.current?.click()
  }

  const fetchSuggestions = async (q) => {
    if (!suggestUrl || q.length < minChars) {
      setSuggestions([])
      setOpen(false)
      return
    }
    const seq = ++fetchSeq.current
    try {
      const res = await fetch(suggestUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ search: q, pagination: { from: 0, size: suggestSize } }),
      })
      const json = await res.json()
      if (seq !== fetchSeq.current) return
      const items = Array.isArray(json?.contents) ? json.contents : []
      setSuggestions(items)
      setOpen(items.length > 0)
    } catch {
      if (seq !== fetchSeq.current) return
      setSuggestions([])
      setOpen(false)
    }
  }

  const onChange = (e) => {
    const q = e.target.value
    setQuery(q)
    if (searchOnType && loadingPath) setLoading(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(q)
      if (searchOnType) commit()
    }, debounceMs)
  }

  const pick = (item) => {
    const label = toLabel(getByPath(item, labelPath))
    setQuery(label != null ? String(label) : '')
    setSuggestions([])
    clearTimeout(debounceRef.current)
    commit()
  }

  const clear = () => {
    setQuery('')
    setSuggestions([])
    clearTimeout(debounceRef.current)
    commit()
  }

  useEffect(() => {
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      clearTimeout(debounceRef.current)
    }
  }, [])

  return (
    <div ref={containerRef} className={`relative ${wrapperClass}`}>
      {/* Input + icons live in their own height-bound group so the loupe and
          clear button center on the INPUT, not on the whole wrapper (which
          also holds the hidden trigger / suggestion list siblings). The icons
          sit in full-height (`inset-y-0`) flex boxes and are centered by
          `items-center` — robust regardless of the icon's intrinsic metrics
          (more reliable than `my-auto` / `-translate-y-1/2` on the svg). */}
      <div className="relative">
        <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
          <Search className="w-4 h-4" />
        </span>
        <input
          type="text"
          {...(inputId ? { id: inputId } : {})}
          value={value}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoComplete="off"
          spellCheck={false}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); clearTimeout(debounceRef.current); commit() }
            else if (e.key === 'Escape') setOpen(false)
          }}
          className={inputClass}
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
            aria-label="Effacer la recherche"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Isolated trigger : the ONLY child of ActionDependant, clicked
          programmatically. Keeps refetch off the input's own clicks/focus. */}
      <ActionDependant {...props} attributesHolderRef={triggerRef}>
        <button ref={triggerRef} type="button" className="hidden" aria-hidden="true" tabIndex={-1} />
      </ActionDependant>

      {open && suggestions.length > 0 && (
        <ul className="absolute left-0 top-full mt-1.5 w-full z-50 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden max-h-72 overflow-y-auto">
          {suggestions.map((item, i) => {
            const label = toLabel(getByPath(item, labelPath))
            const secondary = secondaryPath ? toLabel(getByPath(item, secondaryPath)) : null
            return (
              <li
                key={item.id ?? item.slug ?? i}
                onMouseDown={(e) => { e.preventDefault(); pick(item) }}
                className="flex items-center justify-between gap-3 px-4 py-2.5 cursor-pointer hover:bg-indigo-50 border-b border-gray-50 last:border-0"
              >
                <span className="text-sm font-medium text-gray-800 truncate">{label}</span>
                {secondary && <span className="text-xs text-gray-400 shrink-0">{secondary}</span>}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
